import { useEffect, useState } from "react";

import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useClinic } from "client/clinics/hooks";
import { urls } from "client/routes";
import { sendViewItemList } from "client/analytics/utils";
import CatalogFilters from "client/home/components/CatalogFilters";

import ProductLineCard from "./ProductLineCard";
import BrowseSupport from "./BrowseSupport";
import { useBrowseQuery } from "../hooks";
import PreFooter from "./PreFooter";
import CategoryCard from "./CategoryCard";
import { BrowseAllPatientKind, IBrowseFilterSpeciesKind } from "../types";

import styles from "../styles/BrowseCatalog.module.less";

const VIEW_NAME = "Browse Catalog";

const BrowseCatalog = () => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  const clinic = useClinic();
  const { data: browse, isLoading, error } = useBrowseQuery({ species });

  // Send item list to GA4
  useEffect(() => {
    if (browse?.categories) {
      browse?.categories.forEach((cat) =>
        sendViewItemList(cat.product_lines, `${VIEW_NAME} - ${cat.name}`),
      );
    }
    if (browse?.featured_product_lines) {
      sendViewItemList(browse.featured_product_lines, `${VIEW_NAME} - Featured`);
    }
  }, [browse?.categories, browse?.featured_product_lines]);

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !browse) {
    return <FetchErrorContainer error={error} entityName="categories" isPlural={true} />;
  }

  return (
    <div>
      <Body>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.titles}>
              <div className={styles.label}>Welcome to our new home delivery portal</div>
              <h1 className={styles.title}>Browse online store</h1>
            </div>
            <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
          </div>
          <div className={styles.subheading}>
            <HorizontalOverflow
              direction="right"
              classNames={{ scroller: styles.categories, next: styles.next, back: styles.back }}
            >
              {browse.categories.map((category) => (
                <CategoryCard.Small
                  key={category.id}
                  category={category}
                  className={styles.smallCard}
                />
              ))}
            </HorizontalOverflow>
          </div>
        </div>
      </Body>
      <div className={styles.main}>
        <Container>
          <div className={styles.categories}>
            <div className={styles.category}>
              <div className={styles.info}>
                <div className={styles.titles}>
                  <h3 className={styles.title}>
                    Browse <span className={styles.accent}>popular</span> medications
                  </h3>
                </div>
              </div>
              <HorizontalOverflow
                direction="right"
                classNames={{ scroller: styles.products, button: styles.button }}
              >
                <CategoryCard.TopSellers className={styles.card} />
                {browse.featured_product_lines.map((productLine) => (
                  <div key={productLine.id} className={styles.card}>
                    <ProductLineCard productLine={productLine} eventViewName={VIEW_NAME} />
                  </div>
                ))}
              </HorizontalOverflow>
            </div>
            {browse.categories.map((category) => (
              <div className={styles.category} key={category.id}>
                <div className={styles.info}>
                  <div className={styles.titles}>
                    <h3 className={styles.title}>{category.extended_name}</h3>
                  </div>
                  <PillButton
                    size="s"
                    variant="secondary"
                    dest={urls.products.category({ params: { id: category.id } })}
                  >
                    View All
                  </PillButton>
                </div>
                <HorizontalOverflow
                  direction="right"
                  classNames={{ scroller: styles.products, button: styles.button }}
                >
                  <CategoryCard category={category} className={styles.card} />
                  {category.product_lines.map((productLine) => (
                    <div key={productLine.id} className={styles.card}>
                      <ProductLineCard productLine={productLine} eventViewName={VIEW_NAME} />
                    </div>
                  ))}
                </HorizontalOverflow>
              </div>
            ))}
          </div>
        </Container>
      </div>
      <div className={styles.support}>
        <Container>
          <BrowseSupport />
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </div>
  );
};

export default BrowseCatalog;
